"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LLM_PLATFORMS = exports.AIVisibilityResultSchema = exports.KeywordResearchResultSchema = exports.KeywordClusterSchema = exports.KeywordEntrySchema = exports.KeywordIntentSchema = exports.UpdateSeoConfigSchema = exports.SeoConfigSchema = exports.SeoPromptEntrySchema = exports.SeoCompetitorSchema = exports.SeoPromptCategorySchema = void 0;
const v4_1 = require("zod/v4");
const ai_visibility_1 = require("./ai-visibility");
Object.defineProperty(exports, "LLM_PLATFORMS", { enumerable: true, get: function () { return ai_visibility_1.LLM_PLATFORMS; } });
exports.SeoPromptCategorySchema = v4_1.z.enum([
    "brand",
    "category",
    "local",
    "competitor",
]);
exports.SeoCompetitorSchema = v4_1.z.object({
    name: v4_1.z.string().min(1).max(255),
    domain: v4_1.z.string().min(3).max(255),
});
exports.SeoPromptEntrySchema = v4_1.z.object({
    prompt: v4_1.z.string().min(1).max(500),
    category: exports.SeoPromptCategorySchema,
});
exports.SeoConfigSchema = v4_1.z.object({
    market: v4_1.z.string().max(255),
    locationCode: v4_1.z.number().int().nullable(),
    languageCode: v4_1.z.string().max(10),
    competitors: v4_1.z.array(exports.SeoCompetitorSchema).max(6),
    trackedKeywords: v4_1.z.array(v4_1.z.string().min(1).max(255)).max(30),
    promptSet: v4_1.z.array(exports.SeoPromptEntrySchema).max(20),
    seededAt: v4_1.z.string().nullable(),
    reviewedAt: v4_1.z.string().nullable(),
});
exports.UpdateSeoConfigSchema = exports.SeoConfigSchema.omit({
    seededAt: true,
    reviewedAt: true,
}).extend({
    // Saving/reviewing a profile must leave it usable — an empty tracked-keyword
    // or prompt list would otherwise pass "setup required" gates downstream
    // (keyword research, AI visibility) with nothing to run. SeoConfigSchema
    // itself stays permissive since seeded-but-unreviewed configs may be
    // partial; only this save/update path enforces non-empty lists.
    trackedKeywords: v4_1.z.array(v4_1.z.string().min(1).max(255)).min(1).max(30),
    promptSet: v4_1.z.array(exports.SeoPromptEntrySchema).min(1).max(20),
});
exports.KeywordIntentSchema = v4_1.z.enum([
    "informational",
    "commercial",
    "transactional",
    "navigational",
]);
exports.KeywordEntrySchema = v4_1.z.object({
    keyword: v4_1.z.string(),
    volume: v4_1.z.number().nullable(),
    difficulty: v4_1.z.number().nullable(),
    cpc: v4_1.z.number().nullable(),
});
exports.KeywordClusterSchema = v4_1.z.object({
    title: v4_1.z.string(),
    slug: v4_1.z.string(),
    intent: exports.KeywordIntentSchema,
    keywords: v4_1.z.array(exports.KeywordEntrySchema).min(1),
    serpChecked: v4_1.z.boolean(),
    serpWeak: v4_1.z.boolean().nullable(),
    weaknessReasons: v4_1.z.array(v4_1.z.string()),
    buildRank: v4_1.z.number().int().nullable(),
});
exports.KeywordResearchResultSchema = v4_1.z.object({
    month: v4_1.z.string().regex(/^\d{4}-\d{2}$/),
    coverage: v4_1.z.object({
        expanded: v4_1.z.number().int(),
        serpsChecked: v4_1.z.number().int(),
        serpCheckCap: v4_1.z.number().int(),
    }),
    heroStats: v4_1.z.object({
        keywordsTotal: v4_1.z.number().int(),
        volumeTotal: v4_1.z.number(),
        clustersTotal: v4_1.z.number().int(),
        weakSerps: v4_1.z.number().int(),
    }),
    clusters: v4_1.z.array(exports.KeywordClusterSchema),
    cutList: v4_1.z.array(v4_1.z.object({ keyword: v4_1.z.string(), reason: v4_1.z.string() })),
});
const SovPercent = v4_1.z.number().min(0).max(100);
exports.AIVisibilityResultSchema = v4_1.z.object({
    month: v4_1.z.string().regex(/^\d{4}-\d{2}$/),
    coverage: v4_1.z.object({
        promptsChecked: v4_1.z.number().int(),
        engines: v4_1.z.array(v4_1.z.string()),
        enginesFailed: v4_1.z.array(v4_1.z.string()),
        // First error reason per failed engine, keyed by engine name. Optional:
        // results stored before this field existed must keep validating.
        failureReasons: v4_1.z.record(v4_1.z.string(), v4_1.z.string()).optional(),
    }),
    sov: v4_1.z.object({
        blended: SovPercent.nullable(),
        byEngine: v4_1.z.record(v4_1.z.string(), SovPercent.nullable()),
    }),
    competitors: v4_1.z.array(v4_1.z.object({
        name: v4_1.z.string(),
        domain: v4_1.z.string(),
        sovBlended: SovPercent.nullable(),
        byEngine: v4_1.z.record(v4_1.z.string(), SovPercent.nullable()),
    })),
    matrix: v4_1.z.array(v4_1.z.object({
        prompt: v4_1.z.string(),
        category: exports.SeoPromptCategorySchema,
        results: v4_1.z.record(v4_1.z.string(), v4_1.z
            .object({
            cited: v4_1.z.boolean(),
            mentioned: v4_1.z.boolean(),
            context: v4_1.z.string().nullable(),
        })
            .nullable()),
    })),
    citedSources: v4_1.z.array(v4_1.z.object({ url: v4_1.z.string(), count: v4_1.z.number().int() })),
    baseline: v4_1.z.boolean(),
    // AI-written analysis of this run. Optional for the same reason as
    // `failureReasons` above: results stored before this field existed must
    // keep validating, and generation is allowed to fail without failing
    // the run.
    summary: v4_1.z.string().optional(),
    /** Snapshot of Listing Health at run time (optional for older reports). */
    listingHealthScore: SovPercent.optional(),
});
